/* eslint-disable no-console */
import { useEffect } from 'react';
import { ExtensionHandler } from 'src/utils/extension.utils';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import Footer from './dc-97/dcFooter/Footer';
import AddCustomerFeedback from '../CaseDetails/feedbackForm/component/AddCustomerFeedback';
import { InteractionType } from '../CaseDetails/feedbackForm/interfaces/index.type';
import { DateFormat, getFormatDate } from 'src/utils/DateHelper';
import { FeedbackTypesCodeMap } from '../Cases/constants/CasesConstants';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { HRC_CLICKSTREAM, HRC_EVENTS } from '../../service/clickStream.constant';
import Loader from '../../components/Loader/Loader';
import { GenericObject } from '@universal-call-sdk/adapter-ameyo/lib/types';
import HrSummaryCard from '../DashboardV2/HrSummaryCard';
import WillingToPayCard from '../DashboardV2/willingToPayCard';
import AmountDueCard from '../DashboardV2/AmountDueCard';
import SendPaymentLinkCard from '../DashboardV2/SendPaymentLinkCard';
import styles from '../DashboardV2/HrDashboard.module.scss';

interface HrCustomerDetailsProps {
  resetCallData: () => void;
  disConnectHandler: () => void;
  getLatestCallState: () => string;
  callState: GenericObject;
}

const emiPenaltyChargeOnHold = 'On hold';

const HrCustomerDetails = (props: HrCustomerDetailsProps) => {
  const { resetCallData, disConnectHandler, getLatestCallState, callState } = props;

  const isCallConnected = useSelector((state: RootState) => state.humanReminder.isCallConnected);
  const isFeedbackBtnEnable = useSelector(
    (state: RootState) => state.humanReminder.isFeedbackBtnEnable
  );
  const humanReminderCustomerDetails = useSelector(
    (state: RootState) => state.common.humanReminderCustomerDetails
  );
  const customerDetails = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.customerDetails
  );
  const loanDetails = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.loanDetails
  );
  const previousInteractionDetails = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.previousInteractionDetails
  );
  const reminderType = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.reminderType
  );
  const loading = useSelector(
    (state: RootState) => state.common.humanReminderCustomerDetailsLoading
  );
  const isAmeyoErroneous = useSelector((state: RootState) => state.humanReminder.isAmeyoErronous);
  const callId = useSelector((state: RootState) => state.humanReminder?.callId);

  useEffect(() => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.DASHBOARD_SHOWN], {
      currentCallStatus: ExtensionHandler.currentCallStatus()
    });
    const handleTabClose = (event: any) => {
      event.preventDefault();
      event.returnValue = 'Are you sure you want to exit?';
      return event;
    };
    window.addEventListener('beforeunload', handleTabClose);

    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, []);

  useEffect(() => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.DASHBOARD_DETAILS_SHOWN], {
      1: { customerDetails },
      2: { currentCallStatus: ExtensionHandler.currentCallStatus() },
      3: humanReminderCustomerDetails,
      4: loanDetails
    });
  }, [customerDetails, humanReminderCustomerDetails, loanDetails]);

  const genderAndAge = `${customerDetails?.gender || '--'}, ${customerDetails?.age || '--'}`;
  const lastUpdatedDue = loanDetails?.unpaidDues?.length ? loanDetails?.unpaidDues[0] : null;
  const emiPenaltyChargeStatus =
    lastUpdatedDue?.unpaidPenaltyCharges?.amount === 0 &&
    lastUpdatedDue?.heldPenaltyCharge?.amount > 0
      ? emiPenaltyChargeOnHold
      : lastUpdatedDue?.penaltyApplied?.amount;

  const showFeedbackOptions =
    loanDetails?.loanAccountNumber && customerDetails?.customerReferenceId;

  return (
    <div className={styles.container}>
      <div className={styles.customerDetailsContainer}>
        <div className={styles.customerInfo}>
          <div className={styles.customerSummaryCard}>
            <HrSummaryCard
              customerName={customerDetails?.name || '--'}
              genderAndAge={genderAndAge}
              state={customerDetails?.state || '--'}
              lanNumber={loanDetails?.loanAccountNumber || '--'}
              disbursedAmount={loanDetails?.disbursedAmount?.amount || 0}
              customerReferenceId={customerDetails?.customerReferenceId}
              phoneNumber={customerDetails?.connectedNumber}
              totalTenureMonths={loanDetails?.totalTenureMonths}
              remainingAmount={loanDetails?.remainingAmount?.amount}
              remainingTenureMonths={loanDetails?.remainingTenureMonths}
              lastPaidDpd={loanDetails?.lastPaidDpd}
              currentDpd={loanDetails?.currentDpd}
              duesPaidLate={loanDetails?.duesPaidLate}
              duesPaidOnTime={loanDetails?.duesPaidOnTime}
              lastPaidDate={loanDetails?.lastPaidTimestamp}
            />
          </div>
          <div className={styles.willingPayInfoCard}>
            <WillingToPayCard
              isFirstInteraction={
                !previousInteractionDetails || !previousInteractionDetails?.callTimestamp
              }
              previousCallDate={
                previousInteractionDetails?.callTimestamp
                  ? getFormatDate(
                      new Date(previousInteractionDetails.callTimestamp),
                      DateFormat.DD_MMM_YYYY_HH_MM
                    )
                  : '--'
              }
              willingToPayDate={
                previousInteractionDetails?.feedbackDate
                  ? getFormatDate(previousInteractionDetails.feedbackDate, DateFormat.DD_MMM_YYYY)
                  : '--'
              }
              willingToPayText={
                FeedbackTypesCodeMap[previousInteractionDetails?.feedbackText || '']?.label ||
                previousInteractionDetails?.feedbackText ||
                '--'
              }
              interactionType={previousInteractionDetails?.interactionType}
              ptpInfoIfAny={previousInteractionDetails?.ptpDetailsIfAny || {}}
            />
          </div>
        </div>
        <div className={styles.customerDueEmiDetails}>
          <AmountDueCard
            totalAmount={loanDetails?.totalAmountDue?.amount || 0}
            emiDueDate={
              lastUpdatedDue?.emiDueDate
                ? getFormatDate(new Date(lastUpdatedDue.emiDueDate), DateFormat.DD_MMM_YYYY)
                : '--'
            }
            emiAmount={lastUpdatedDue?.emiAmount?.amount || 0}
            emiDaysRemaining={loanDetails?.currentDpd || 0}
            emiPenaltyCharge={emiPenaltyChargeStatus || 0}
            emiIncreasePenaltyChargeDate={
              lastUpdatedDue?.nextIncrementDate
                ? getFormatDate(new Date(lastUpdatedDue?.nextIncrementDate), DateFormat.DD_MMM_YYYY)
                : '--'
            }
            emiIncreasePenalChargeAmount={lastUpdatedDue?.nextIncrementAmount?.amount || 0}
            emiAmountWithIncrementalPenaltyCharge={
              lastUpdatedDue?.emiAmountWithIncrementalPenaltyCharge?.amount || 0
            }
            mandateStatus={loanDetails?.mandateStatus}
            repaymentFailureDetails={loanDetails?.repaymentFailureDetails}
            upcomingDueDetails={loanDetails?.upcomingDueDetails}
            reminderType={reminderType}
            totalUnpaidDueAmount={lastUpdatedDue?.totalUnpaidDueAmount?.amount || 0}
            totalEmiAmount={lastUpdatedDue?.totalEmiAmount?.amount || 0}
            penaltyApplied={lastUpdatedDue?.penaltyApplied?.amount || 0}
            nextIncrementAmount={lastUpdatedDue?.nextIncrementAmount?.amount || 0}
            paidAmount={lastUpdatedDue?.paidAmount?.amount || 0}
            heldPenaltyCharge={lastUpdatedDue?.heldPenaltyCharge?.amount || 0}
            holdEligibleDues={loanDetails?.holdEligibilityDues?.[0]}
            nextDayDues={loanDetails?.nextDayDues?.[0]}
          />
        </div>
        <SendPaymentLinkCard
          primaryNumber={customerDetails?.primaryNumber}
          connectedNumber={customerDetails?.connectedNumber}
        />
        <Footer
          isCallEnded={!isCallConnected}
          disConnectHandler={disConnectHandler}
          getLatestCallState={getLatestCallState}
          callState={callState}
        />
      </div>
      <div className={styles.sidebarSection}>
        {/*<FeedbackFrom isDisableNextPreviousCaseAction />*/}
        {showFeedbackOptions ? (
          <AddCustomerFeedback
            callId={callId}
            interactionType={InteractionType.HUMAN_REMINDER}
            referenceId={humanReminderCustomerDetails?.humanReminderResponse?.reminderRefId}
            loanId={
              humanReminderCustomerDetails?.humanReminderResponse?.loanDetails?.loanAccountNumber
            }
            customerId={customerDetails?.customerReferenceId}
            interactionId={humanReminderCustomerDetails?.humanReminderResponse?.interactionRefId}
            feedbackSubmitEnabled={isFeedbackBtnEnable || isAmeyoErroneous}
            successCallback={resetCallData}
            commentRequired={false}
            connectedPhoneNumber={customerDetails?.connectedNumber}
          />
        ) : null}
      </div>
      <Loader show={loading} className={'overlay'} animate={false} />
    </div>
  );
};

export default HrCustomerDetails;
